import { observer } from 'mobx-react'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateFormat, Project } from '../../../models3/Project'

import { Root } from '../../../models3/Root'
import { EditButton } from '../../utils/Buttons'
import { displayError } from '../../utils/Errors'
import { GenericIcon } from '../../utils/Icons'
import { ILocalizedChoice, MakeLocalizedChoice } from '../../utils/MakeLocalizedChoice'
import { EditableRichText } from '../../utils/RichTextEditor'
import TextInput from '../../utils/TextInput'
import { isValidDisplayNameCharacters, isValidDisplayNameLength } from '../../utils/Helpers'
import { NoteMarkerColorsPreferences } from './NoteMarkerColorsPreferences'
import { RecordingLayoutPreferences } from './RecordingLayoutPreferences'
import { VideoCompressionPreferences } from './VideoCompressionPreferences'
import '../ProjectSettings.css'

type ProjectPreferencesProps = {
    rt: Root
}

const iconClassName = 'project-settings-icon right-margin-15px fa-fw'

type DisplayNameProps = {
    allowEditing: boolean
    displayName: string
    setDisplayName: (displayName: string) => void
}

const DisplayName: FC<DisplayNameProps> = observer(({ allowEditing, displayName, setDisplayName }) => {
    const { t } = useTranslation()
    const [editing, setEditing] = useState(false)

    function validate(value: string) {
        const newName = value.trim()
        if (!isValidDisplayNameCharacters(newName)) {
            return t('Invalid characters')
        }

        if (!isValidDisplayNameLength(newName)) {
            return t('Too short or long')
        }

        return ''
    }

    return (
        <div className="team-preference-option">
            <GenericIcon className={iconClassName} iconName="fa-flag" tooltip={t('Project name')} />
            {editing ? (
                <TextInput
                    validate={validate}
                    initialValue={displayName}
                    message={t('Type Enter to change project name or type Esc to cancel')}
                    _onEnter={(name) => {
                        if (!validate(name)) {
                            setDisplayName(name)
                        }
                        setEditing(false)
                    }}
                    _onEscape={() => setEditing(false)}
                    allowEmptyValue={false}
                />
            ) : (
                <div className="project-display-name right-margin-15px">{displayName}</div>
            )}
            {!editing && (
                <EditButton
                    enabled={allowEditing}
                    onClick={() => setEditing(true)}
                    className="project-preferences-edit-button"
                    tooltip={t('Edit')}
                />
            )}
        </div>
    )
})

type ProjectTextProps = {
    allowEditing: boolean
    text: string
    setText: (value: string) => void
}

const ProjectText: FC<ProjectTextProps> = observer(({ allowEditing, text, setText }) => {
    const { t } = useTranslation()
    const [editing, setEditing] = useState(false)

    const prefix = 'project-text'
    return (
        <div className="team-preference-option">
            <GenericIcon className={iconClassName} iconName="fa-comment-alt" tooltip={t('Project description')} />
            <EditableRichText
                savedText={text}
                save={setText}
                cancel={() => {}}
                editorOpen={editing}
                setEditorOpen={setEditing}
                prefix={prefix}
            />
            {!editing && (
                <EditButton
                    enabled={allowEditing}
                    onClick={() => setEditing(true)}
                    className="project-preferences-edit-button"
                    tooltip={t('Edit')}
                />
            )}
        </div>
    )
})

type IProjectType = {
    allowEditing: boolean
    project: Project
}

const ProjectType: FC<IProjectType> = ({ allowEditing, project }) => {
    const { t } = useTranslation()

    const [projectType, setProjectType] = useState(project.projectType)

    const projectTypes: ILocalizedChoice[] = [
        {
            choice: '',
            localizedChoice: t('Project type not set'),
            title: ''
        },
        {
            choice: 'translation',
            localizedChoice: t('Translation Project'),
            title: ''
        },
        {
            choice: 'additional',
            localizedChoice: t('Additional Translation Project'),
            title: t(`When a team has multiple projects mark all but the first as 'Additional'`)
        },
        {
            choice: 'test_training',
            localizedChoice: t('Test or Training Project'),
            title: ''
        },
        {
            choice: 'resource',
            localizedChoice: t('Translation resource'),
            title: ''
        },
        {
            choice: 'other',
            localizedChoice: t('Other Project'),
            title: t('Discussion forum, resource development, etc.')
        }
    ]
    const choice = projectTypes.find((pt) => pt.choice === projectType) ?? projectTypes[0]

    return (
        <div className="team-preference-option">
            <GenericIcon className={iconClassName} iconName="fa-certificate" tooltip={t('Project type')} />
            {allowEditing ? (
                <MakeLocalizedChoice
                    choices={projectTypes}
                    choice={projectType}
                    setChoice={(_choice) => {
                        project.setProjectType(_choice).catch(displayError)
                        setProjectType(_choice as any)
                    }}
                />
            ) : (
                <span>{choice.localizedChoice}</span>
            )}
        </div>
    )
}

type IProjectRegion = {
    allowEditing: boolean
    project: Project
}

const ProjectRegion: FC<IProjectRegion> = ({ allowEditing, project }) => {
    const { t } = useTranslation()
    const [projectRegion, setProjectRegion] = useState(project.region)

    const projectRegions: ILocalizedChoice[] = [
        {
            choice: '',
            localizedChoice: t('Region not set'),
            title: ''
        },
        {
            choice: 'africa',
            localizedChoice: t('Africa'),
            title: ''
        },
        {
            choice: 'americas',
            localizedChoice: t('Americas'),
            title: ''
        },
        {
            choice: 'asia',
            localizedChoice: t('Asia'),
            title: ''
        },
        {
            choice: 'europe',
            localizedChoice: t('Europe'),
            title: ''
        },
        {
            choice: 'oceania',
            localizedChoice: t('Oceania'),
            title: ''
        }
    ]

    const choice = projectRegions.find((pr) => pr.choice === projectRegion) ?? projectRegions[0]

    return (
        <div className="team-preference-option">
            <GenericIcon className={iconClassName} iconName="fa-globe" tooltip={t('Project region')} />
            {allowEditing ? (
                <MakeLocalizedChoice
                    choices={projectRegions}
                    choice={projectRegion}
                    setChoice={(_choice) => {
                        project.setRegion(_choice).catch(displayError)
                        setProjectRegion(_choice as any)
                    }}
                />
            ) : (
                <span>{choice.localizedChoice}</span>
            )}
        </div>
    )
}

interface IDateFormatSelector {
    dateFormat: DateFormat
    setDateFormat: (format: DateFormat) => void
}

const DateFormatSelector: FC<IDateFormatSelector> = observer(({ dateFormat, setDateFormat }) => {
    const { t } = useTranslation()
    const values = Object.values(DateFormat)

    return (
        <div className="team-preference-option">
            <GenericIcon className={iconClassName} iconName="fa-calendar-alt" tooltip={t('Date format')} />
            <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value as DateFormat)}>
                {values.map((item) => (
                    <option key={item} value={item}>
                        {item}
                    </option>
                ))}
            </select>
        </div>
    )
})

const ProjectPreferences: FC<ProjectPreferencesProps> = observer(({ rt }) => {
    const { t } = useTranslation()
    const { iAmAdmin, project } = rt
    const { displayName, setDisplayName, text, setText } = project
    const { dateFormat } = project
    let { setDateFormat } = project

    setDateFormat = setDateFormat.bind(rt.project)
    const allowEditing = iAmAdmin

    return (
        <div className="project-preferences-tab">
            <h3>{t('Project Preferences')}</h3>
            <DisplayName {...{ displayName, setDisplayName, allowEditing: allowEditing && !rt.groupProjects }} />
            <ProjectText {...{ allowEditing, text, setText }} />
            <ProjectType {...{ project, allowEditing }} />
            <ProjectRegion {...{ project, allowEditing }} />
            {allowEditing && (
                <>
                    <DateFormatSelector {...{ dateFormat, setDateFormat }} />
                    <NoteMarkerColorsPreferences project={project} iconClassName={iconClassName} />
                    <RecordingLayoutPreferences project={project} iconClassName={iconClassName} />
                    <VideoCompressionPreferences project={project} iconClassName={iconClassName} />
                </>
            )}
        </div>
    )
})

export default ProjectPreferences
